import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { isMobile } from 'react-device-detect';
import { Abhaya_Libre } from 'next/font/google';
import ContentWrapper from '../../common/ContentWrapper';
import Link from '../../common/Link/Link';
import useIsAboveBreakpoint from '../../../core/hooks/useIsAboveBreakpoint';
import { Locale } from '../../../core/constants';

const abhayaLibre = Abhaya_Libre({
  weight: '600',
  subsets: ['latin'],
});

const SNSBanner = () => {
  const router = useRouter();
  const { t } = useTranslation();

  const isAboveMDBreakpoint = useIsAboveBreakpoint('MD');

  const youtubeUrl = process.env.NEXT_PUBLIC_YOUTUBE_URL || '#';
  const jaInstagramUrl = process.env.NEXT_PUBLIC_JA_INSTAGRAM_URL || '#';
  const enInstagramUrl = process.env.NEXT_PUBLIC_EN_INSTAGRAM_URL || '#';
  const lineUrl = process.env.NEXT_PUBLIC_LINE_URL || '#';
  const twitterUrl = process.env.NEXT_PUBLIC_TWITTER_URL || '#';
  const facebookUrl = process.env.NEXT_PUBLIC_FACEBOOK_URL || '#';
  const snsIconSize = isAboveMDBreakpoint ? 64 : 48;

  const dynamicUrl = isMobile && process.env.NEXT_PUBLIC_DYNAMIC_APP_URL;
  const appStoreUrl = process.env.NEXT_PUBLIC_APP_STORE_URL || '#';
  const googlePlayStoreUrl = process.env.NEXT_PUBLIC_GOOGLE_PLAY_STORE_URL || '#';

  const snsLinks = (
    <div className="flex h-full justify-center gap-6 align-middle md:gap-8">
      <Link
        href={router?.locale === Locale.JAPANESE ? jaInstagramUrl : enInstagramUrl}
        prefetch={false}
      >
        <a target="_blank" rel="noopener" className="flex items-center">
          <Image
            src="/instagram.svg"
            alt="instagram logo"
            width={snsIconSize}
            height={snsIconSize}
            layout="fixed"
          />
        </a>
      </Link>
      {router.locale === Locale.JAPANESE && (
        <Link href={facebookUrl} prefetch={false}>
          <a target="_blank" rel="noopener" className="flex items-center">
            <Image
              src="/facebook_white.svg"
              alt="facebook logo"
              width={snsIconSize}
              height={snsIconSize}
              layout="fixed"
            />
          </a>
        </Link>
      )}
      <Link href={youtubeUrl} prefetch={false}>
        <a target="_blank" rel="noopener" className="flex items-center">
          <Image
            src="/youtube.svg"
            alt="youtube logo"
            width={snsIconSize}
            height={snsIconSize}
            layout="fixed"
          />
        </a>
      </Link>
      {router.locale === Locale.JAPANESE && (
        <>
          <Link href={twitterUrl} prefetch={false}>
            <a target="_blank" rel="noopener" className="flex items-center">
              <Image
                src="/x.svg"
                alt="x logo"
                width={snsIconSize}
                height={snsIconSize}
                layout="fixed"
              />
            </a>
          </Link>
          <Link href={lineUrl} prefetch={false}>
            <a target="_blank" rel="noopener" className="flex items-center">
              <Image
                src="/line_icon.svg"
                alt="line logo"
                width={snsIconSize}
                height={snsIconSize}
                layout="fixed"
              />
            </a>
          </Link>
        </>
      )}
    </div>
  );

  if (router.locale === Locale.JAPANESE) {
    return (
      <ContentWrapper className="mt-12 px-0 sm:mt-16 sm:px-0 md:px-16 sm-max:px-0">
        <div className="flex h-40 w-full md:skew-x-[-24deg]">
          <div className="flex h-full flex-1 items-center justify-center bg-black p-6 md:flex-row md:gap-8 md:rounded-l-md">
            <div className="flex flex-col items-center gap-4 md:skew-x-[24deg]">
              <Image
                src="/follow-us-on-ja.svg"
                alt="Follow us on"
                layout="fixed"
                width={200}
                height={68}
              />
              {snsLinks}
            </div>
          </div>
          <div className="hidden h-full flex-1 flex-col items-center justify-center gap-4 rounded-r-md bg-gray-50 p-6 md:block md:flex-row md:gap-8">
            <div className="flex h-full skew-x-[24deg] flex-col items-center justify-center gap-4">
              <p className="text-xl text-gray-600 lg:text-2xl">
                {t('component:app_download_links.title')}
              </p>
              <div className="flex gap-6">
                <Link href={dynamicUrl || appStoreUrl} prefetch={false}>
                  <a target="_blank" rel="noopener" className="flex">
                    <Image
                      src="/app-store-badge.svg"
                      alt="App store badge"
                      width={173}
                      height={64}
                    />
                  </a>
                </Link>
                <Link href={dynamicUrl || googlePlayStoreUrl} prefetch={false}>
                  <a target="_blank" rel="noopener" className="flex">
                    <Image
                      src="/google-play-badge.svg"
                      alt="Google Play store badge"
                      width={216}
                      height={64}
                    />
                  </a>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper className="mt-12 px-0 sm:mt-16 sm:px-0 md:px-16 sm-max:px-0">
      <div className="w-full bg-black md:skew-x-[-24deg] md:rounded-md">
        <div className="flex h-36 flex-col items-center justify-center gap-3 p-6 md:h-24 md:skew-x-[24deg] md:flex-row md:gap-8">
          <p className={`text-2xl text-white ${abhayaLibre.className}`}>Follow us on:</p>
          {snsLinks}
        </div>
      </div>
    </ContentWrapper>
  );
};

export default SNSBanner;
